import { Component, computed, inject, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { RelativeTimePipe, TruncatePipe } from '@htx/core';
import {
  Policy,
  type Constraint,
  policyAutoName,
  policyAutoDescription,
  constraintLabel,
  constraintIcon
} from '@htx/policies';

@Component({
  selector: 'app-policy-overview-card',
  imports: [
    MatCard,
    MatCardContent,
    MatIcon,
    MatIconButton,
    NgClass,
    RelativeTimePipe,
    TruncatePipe,
    TranslocoPipe
  ],
  templateUrl: './policy-overview-card.component.html',
  styleUrl: './policy-overview-card.component.scss'
})
export class PolicyOverviewCardComponent {
  private readonly translocoService = inject(TranslocoService);

  readonly policy = input.required<Policy>();
  readonly showDelete = input(false);
  readonly deleteClick = output<void>();
  readonly titleClick = output<void>();

  readonly title = computed(() => policyAutoName(this.translocoService, this.policy()));
  readonly description = computed(() => policyAutoDescription(this.translocoService, this.policy()));

  readonly severity = computed(() => {
    const constraints = this.allConstraints();
    if (constraints.length === 0) return 'open';
    if (constraints.length === 1) return 'moderate';
    if (constraints.length >= 3) return 'strict';
    return 'combined';
  });

  readonly typeIcon = computed(() => {
    const constraints = this.allConstraints();
    if (constraints.length === 0) return 'lock_open';
    if (constraints.length === 1) return constraintIcon(constraints[0].type);
    return 'playlist_add_check';
  });

  readonly typeBadge = computed(() => {
    const constraints = this.allConstraints();
    if (constraints.length === 0) {
      return this.translocoService.translate('policy.card.badge.open');
    }
    if (constraints.length === 1) {
      return constraintLabel(this.translocoService, constraints[0]);
    }
    return this.translocoService.translate('policy.card.badge.and', { count: constraints.length });
  });

  private readonly allConstraints = computed((): Constraint[] => {
    const p = this.policy();
    const rules = p.rules ?? [];
    return rules.length > 0
      ? rules.flatMap((r) => r.constraints ?? [])
      : (p.constraints ?? []);
  });

  onDelete(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit();
  }
}
